import { Phone, MessageCircle, Navigation, Mail, MapPin, Clock } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { SectionHeading } from "../components/SectionHeading";
import { BUSINESS } from "../data/content";

const INFO = [
  { icon: MessageCircle, label: "WhatsApp", value: BUSINESS.whatsappDisplay, href: BUSINESS.whatsappHref, testid: "contact-whatsapp" },
  { icon: Mail, label: "Email", value: BUSINESS.email, href: `mailto:${BUSINESS.email}`, testid: "contact-email" },
  { icon: MapPin, label: "Address", value: BUSINESS.address, href: BUSINESS.mapsUrl, testid: "contact-address" },
  { icon: Clock, label: "Opening Hours", lines: BUSINESS.hoursLines, href: null, testid: "contact-hours" },
];

export const Contact = () => (
  <section id="contact" data-testid="contact-section" className="py-20 md:py-32 bg-[#F7F1E6] scroll-mt-20">
    <div className="max-w-7xl mx-auto px-5 md:px-8">
      <SectionHeading
        chapter="09"
        overline="Visit Us"
        title="Visit Our Showroom"
        description="We would love to welcome you to Jeweller Kalika Prasad. Call, message or drop by — our team is ready to help you find the perfect piece."
      />

      <div className="grid lg:grid-cols-2 gap-10 -mt-4 md:-mt-8">
        <Reveal>
          <div className="space-y-1 border-t border-gold/25">
            <div data-testid="contact-phone" className="flex items-center gap-5 py-5 border-b border-gold/25">
              <span className="w-11 h-11 shrink-0 rounded-full border border-gold/40 flex items-center justify-center">
                <Phone size={17} strokeWidth={1.4} className="text-gold-muted" />
              </span>
              <span>
                <span className="block text-[11px] tracking-[0.25em] uppercase text-charcoal/45 font-medium">Phone</span>
                <span className="block mt-1 space-y-0.5">
                  {BUSINESS.phones.map((p, i) => (
                    <a key={p.href} data-testid={`contact-phone-${i + 1}`} href={p.href} className="block font-serif text-xl text-royal-deep hover:text-gold-muted transition-colors duration-300">
                      {p.display}
                    </a>
                  ))}
                </span>
              </span>
            </div>
            {INFO.map((item) => {
              const Icon = item.icon;
              const inner = (
                <>
                  <span className="w-11 h-11 shrink-0 rounded-full border border-gold/40 flex items-center justify-center">
                    <Icon size={17} strokeWidth={1.4} className="text-gold-muted" />
                  </span>
                  <span>
                    <span className="block text-[11px] tracking-[0.25em] uppercase text-charcoal/45 font-medium">{item.label}</span>
                    {item.lines ? (
                      <span className="block mt-1 space-y-1">
                        {item.lines.map((l) => (
                          <span key={l} className="block font-serif text-lg leading-snug text-royal-deep">{l}</span>
                        ))}
                      </span>
                    ) : (
                      <span className="block mt-1 font-serif text-xl text-royal-deep">{item.value}</span>
                    )}
                  </span>
                </>
              );
              return item.href ? (
                <a
                  key={item.label}
                  data-testid={item.testid}
                  href={item.href}
                  target={item.href.startsWith("http") ? "_blank" : undefined}
                  rel={item.href.startsWith("http") ? "noopener noreferrer" : undefined}
                  className="flex items-center gap-5 py-5 border-b border-gold/25 group"
                >
                  {inner}
                </a>
              ) : (
                <div key={item.label} data-testid={item.testid} className="flex items-center gap-5 py-5 border-b border-gold/25">
                  {inner}
                </div>
              );
            })}
          </div>
          <div className="mt-9 flex flex-wrap gap-3.5">
            <a
              data-testid="contact-call-now-button"
              href={BUSINESS.phoneHref}
              className="px-7 py-3.5 bg-royal text-ivory text-sm tracking-[0.12em] uppercase font-medium rounded-sm hover:bg-royal-light transition-colors duration-300 flex items-center gap-2"
            >
              <Phone size={15} strokeWidth={1.5} /> Call Now
            </a>
            <a
              data-testid="contact-whatsapp-button"
              href={BUSINESS.whatsappHref}
              target="_blank"
              rel="noopener noreferrer"
              className="px-7 py-3.5 bg-gold text-royal-deep text-sm tracking-[0.12em] uppercase font-medium rounded-sm hover:bg-gold-light transition-colors duration-300 flex items-center gap-2"
            >
              <MessageCircle size={15} strokeWidth={1.5} /> WhatsApp Us
            </a>
            <a
              data-testid="contact-directions-button"
              href={BUSINESS.mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="px-7 py-3.5 border border-royal/30 text-royal text-sm tracking-[0.12em] uppercase font-medium rounded-sm hover:bg-royal hover:text-ivory transition-colors duration-300 flex items-center gap-2"
            >
              <Navigation size={15} strokeWidth={1.5} /> Get Directions
            </a>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div data-testid="contact-map" className="relative h-full min-h-[380px] rounded-sm overflow-hidden border border-gold/30">
            <iframe
              title="Jeweller Kalika Prasad showroom location map — Howrah, West Bengal"
              src={BUSINESS.mapsEmbed}
              className="absolute inset-0 w-full h-full grayscale-[35%] contrast-[1.05]"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
            <span className="absolute top-4 left-4 bg-royal-deep/85 backdrop-blur-sm text-ivory text-[11px] tracking-[0.25em] uppercase px-4 py-2 rounded-sm">
              Howrah, West Bengal
            </span>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);
